import React, { FC, useRef } from 'react'

import LoadingSpinner from '@components/shared/LoadingSpinner'
import { TitleType } from '@models/Title'
import { usePageApi } from '@providers/PageApiProvider'
import { SearchPageApiResponse } from '@providers/types/api-responses'
import { ViewType } from '@utils/global'

import GridResults from './GridResults'
import ListResults from './ListResults'
import NoResults from './NoResults'
import useInfiniteTitles from './hooks/useInfiniteTitles'

interface Props {
  layoutType: ViewType
}

const SearchResults: FC<Props> = ({ layoutType }: Props) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const { titles, startPage, page, lastPage } = useInfiniteTitles(scrollRef)
  return (
    <div
      className="px-4 pb-4x flex-1 overflow-y-auto"
      data-testid="ResultsContainer"
      ref={scrollRef}>
      <Results
        titles={titles}
        startPage={startPage}
        layoutType={layoutType}
        hasMore={page < lastPage}
      />
    </div>
  )
}

export default SearchResults

interface ResultsProps {
  titles: TitleType[]
  startPage: number
  layoutType: ViewType
  hasMore: boolean
}
const Results: React.FC<ResultsProps> = ({ titles, startPage, layoutType, hasMore }) => {
  const { loading } = usePageApi<SearchPageApiResponse>()

  // Data is still loading, show loading spinner
  if (loading) {
    return (
      <div className="flex flex-col items-center px-4">
        <LoadingSpinner />
      </div>
    )
  }

  // No titles found
  if (!titles.length) {
    return <NoResults />
  }

  // Render grid or list of titles
  return (
    <>
      {startPage > 1 && <LoadingSpinner />}
      {layoutType === ViewType.LIST ? (
        <ListResults titles={titles} startPage={startPage} />
      ) : (
        <GridResults titles={titles} startPage={startPage} />
      )}
      {hasMore && <LoadingSpinner />}
    </>
  )
}
